<template>
  <div class="m-3" style="height: 90vh">
    <div class="grid grid-cols-2 gap-2">
      <div class="col-span-12 chart-card" style="height: 400px">
        <v-chart :option="option" autoresize />
      </div>
    </div>

    <ReportsTable ref="rtable" :columns="columnDefs" :settings="{title: t('employeesSalaries')}" endpoint="reports/hr/paid-salaries">
      <template #filter>
        <el-select
          filterable
          clearable
          class="col-span-12 md:col-span-3"
          value-key="fullName"
          :placeholder="t('selectEmployee')"
          v-model="selectedEmployee"
          @change="onSelectedEmployeeChanged"
          remote
          :remote-method="searchEmployees"
        >
          <el-option
            v-for="option in employees"
            :key="option.id"
            :label="option.fullName"
            :value="option"
          ></el-option>
        </el-select>
      </template>
    </ReportsTable>
  </div>
</template>

<script setup lang="ts">
import { ref, onBeforeMount, provide } from 'vue'
import { useI18n } from 'vue-i18n'
import { ColDef } from 'ag-grid-community'
import { useRoute, useRouter } from 'vue-router'
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { PieChart, BarChart } from 'echarts/charts'
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  ToolboxComponent,
  DataZoomComponent
} from 'echarts/components'
import VChart, { THEME_KEY } from 'vue-echarts'

import { useHTTP } from '@/plugins/http'
import { useAlertModal } from '@/plugins/alert-modal/alert-modal'
import { formatMoney } from '@/utils/money'
import ReportsTable from '@/views/reports/v2/table/ReportsTable.vue'
import { formatPostgresDate } from '@/utils/date'
import User from '@/models/user/User'

// i18n
const { t } = useI18n({
  messages: {
    en: {
      employeesSalaries: 'Employees Salaries',
      username: 'Username',
      name: 'Name',
      salary: 'Salary',
      Salary: 'Salary',
      topSalaries: 'Top Salaries',
      employee: 'Employee',
      period: 'Period',
      employeeType: 'Employee Type',
      payType: 'Pay Type',
      amount: 'Amount',
      contractor: 'Contractor',
      freelancer: 'Freelancer',
      commission: 'Commission',
      bonus: 'Bonus',
      fees: 'Fees',
      selectEmployee: 'Select Employee',
    },
    ar: {
      employeesSalaries: 'رواتب الموظفين',
      username: 'أسم المستخدم',
      name: 'أسم الموظف',
      salary: 'ألراتب',
      Salary: 'راتب',
      topSalaries: 'رواتب الموظفين',
      employee: 'الموظف',
      period: 'المدة',
      employeeType: 'نوع الموظف',
      payType: 'نوع الدفع',
      amount: 'المبلغ',
      contractor: 'موظف مؤقت او مقاول',
      freelancer: 'عميل',
      commission: 'عمولة',
      bonus: 'مكافئة',
      fees: 'اجور',
      selectEmployee: 'اختر الموظف',
    }
  }
})

use([
  CanvasRenderer,
  PieChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  BarChart,
  GridComponent,
  ToolboxComponent,
  DataZoomComponent
])

provide(THEME_KEY, 'light')

const option = ref({
  title: {
    text: t('topSalaries'),
    left: 'left'
  },
  toolbox: {
    feature: {
      saveAsImage: {}
    }
  },
  tooltip: {
    trigger: 'axis',
    axisPointer: {
      type: 'shadow'
    }
  },
  legend: {},
  grid: {
    left: '3%',
    right: '4%',
    bottom: '3%',
    containLabel: true
  },
  dataZoom: [
    {
      id: 'dataZoomY',
      type: 'slider',
      yAxisIndex: 0,
      filterMode: 'empty',
      startValue: 0,
      endValue: 4,
      zoomLock: true,
      handleSize: 0,
      showDetail: false
    },
    {
      type: 'inside',
      id: 'insideY',
      yAxisIndex: 0,
      startValue: 0,
      endValue: 4,
      zoomOnMouseWheel: false,
      moveOnMouseMove: true,
      moveOnMouseWheel: true
    }
  ],
  xAxis: {
    type: 'value',
    boundaryGap: [0, 0.01],
    axisLabel: {
      formatter: (val: any) => (val ? formatMoney(val, 'IQD') : '')
    }
  },
  yAxis: {
    type: 'category',
    inverse: true,
    data: []
  },
  series: [
    {
      type: 'bar',
      data: [],
      name: t('topSalaries'),
      barWidth: 50,
      label: {
        show: false,
        align: 'center',
        position: 'left',
        distance: -100,
        formatter: (val: any) => {
          return val.data ? formatMoney(val.data, 'IQD') : ''
        }
      }
    }
  ]
})

// Data
const rtable = ref<any>()
const columnDefs = ref<ColDef[]>([])

const http = useHTTP()
const route = useRoute()
const router = useRouter()
const alertModal = useAlertModal()

const employees = ref<User[] | any>();
const selectedEmployee = ref<any>()

onBeforeMount(async () => {
  load()
  columnDefs.value = [
    {
      headerName: t('employee'),
      field: 'employee_name',
      checkboxSelection: true,
      headerCheckboxSelection: true
    },
    {
      headerName: t('period'),
      width: '250',
      valueFormatter: ({ data }) => {
        if (!data) return ''
        return `${formatPostgresDate(data.period_start_date, 'DD/MM/YYYY')} - ${formatPostgresDate(data.period_end_date, 'DD/MM/YYYY')}`
      }
    },
    {
      headerName: t('employeeType'),
      field: 'employee_type',
      valueFormatter: ({ data }) => {
        if (!data) return ''
        return t(data.employee_type)
      }
    },
    {
      width: '100',
      headerName: t('payType'),
      field: 'pay_type',
      valueFormatter: ({ data }) => {
        if (!data) return ''
        return t(data.pay_type)
      }
    },
    {
      headerName: t('amount'),
      field: 'total_amount',
      cellStyle: { direction: 'ltr !important', textAlign: 'center' },
      valueFormatter: ({ data }) => {
        if (!data) return ''
        return data.total_amount != undefined ? formatMoney(data.total_amount, 'IQD') : ''
      }
    },
    {
      headerName: t('models.common.date'),
      field: 'createdAt',
      cellStyle: { direction: 'ltr' },
      valueFormatter: ({ data }) => {
        if (!data) return ''
        console.log(data)
        const createdAt = new Date(data.created_at)
        return formatPostgresDate(String(createdAt), 'DD/MM/YYYY')
      }
    }
  ]
})

// Methods
async function load() {
  try {
    const data = await http.get<any[]>('/reports/hr/top-salaries')
    const chartData: any = []
    data.forEach((i: any) => {
      chartData.push({
        value: i.salary,
        name: `${i.name || ''}`
      })
    })
    option.value.series[0].data = chartData.map((i: any) => i.value)
    option.value.yAxis.data = chartData.map((i: any) => i.name)
  } catch (error) {
    alertModal.showDanger({
      title: error.title,
      body: error.body
    })
  }
}


async function onSelectedEmployeeChanged(event: any) {
  rtable.value.payload.filter = selectedEmployee.value? selectedEmployee.value.id: null;
  rtable.value.navigate(rtable.value.payload)
}

async function searchEmployees(query: string) {
  if (!query) {
    query = ''
  }
  try {
    const url = `${User.ENDPOINT}?q=${query}`
    const employeesRes = await http.get<User[]>(url)
    employees.value = employeesRes.map((employee) => User.from(employee))
  } catch (error) {
    employees.value = []
  }
}
</script>
